// SigmaStudioVariations.jsx

import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import BellCurveChart from "./BellCurveChart";
import BellCurveInsights from "./BellCurveInsights";
import "./SigmaStudio.css";

/* ---------------- SAMPLE DATA ---------------- */

const sampleData = [
  "6:10 AM","5:55 AM","6:20 AM","6:05 AM","6:30 AM",
  "6:15 AM","7:02 AM","5:58 AM","6:25 AM","6:12 AM",
  "5:50 AM","7:08 AM","6:40 AM","6:18 AM","5:57 AM",
  "6:07 AM","7:22 AM","6:03 AM","6:35 AM","5:56 AM",
  "6:11 AM","6:28 AM","6:01 AM","6:17 AM","6:04 AM",
  "5:53 AM","6:14 AM","6:32 AM","6:19 AM","5:59 AM",
  "6:06 AM","6:24 AM","5:54 AM","6:13 AM","6:38 AM",
  "6:09 AM","6:27 AM","6:16 AM","6:00 AM","5:51 AM",
  "6:21 AM","6:36 AM","7:52 AM","6:29 AM","6:10 AM"
];

/* ---------------- UTILITIES ---------------- */

const normalizeValue = (value) => {

  if (typeof value === "number") return value;

  if (typeof value === "string") {

    const trimmed = value.trim();

    /* numeric */
    if (!isNaN(trimmed)) return Number(trimmed);

    /* time */
    if (trimmed.includes(":")) {

      const [time, period] = trimmed.split(" ");
      const [hours, minutes] = time.split(":").map(Number);

      let total = hours * 60 + minutes;

      if (period === "PM" && hours !== 12) total += 720;
      if (period === "AM" && hours === 12) total -= 720;

      return total;
    }
  }

  return null;
};

const validateData = (values) => {

  const cleaned = values
    .map(v => normalizeValue(v))
    .filter(v => v !== null);

  if (cleaned.length < 5) {
    return { valid:false, message:"Minimum 5 valid values required." };
  }

  return { valid:true, data:cleaned };
};

/* ---------------- COMPONENT ---------------- */

const SigmaStudioVariations = () => {

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [mode, setMode] = useState(null);
  const [inputTitle, setInputTitle] = useState("");
  const [inputData, setInputData] = useState("");
  const [chartXLabel, setChartXLabel] = useState("");
  const [chartYLabel, setChartYLabel] = useState("");

  const [chartData, setChartData] = useState([]);
  const [error, setError] = useState("");

  /* ---------------- AUTH ---------------- */

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  /* ---------------- ACTIONS ---------------- */

  const showSample = () => {

    setMode("sample");
    setChartData(sampleData);
    setChartXLabel("Time");
    setChartYLabel("Frequency");
    setInputTitle("Wake Up Time")
    setError("");
  };

  const prepareMine = () => {

    setMode("input");
    setChartData([]);
    setError("");
  };

  const handleShowCurve = () => {

    const values = inputData.split(",");

    const result = validateData(values);

    if (!result.valid) {
      setError(result.message);
      return;
    }

    setError("");
    setChartData(result.data);
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="min-h-screen flex flex-col bg-black text-gray-100 font-sans">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">

        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            🎛️ Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">
            Variations Visualisation (Bell Curve) Playground
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">

          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">
                {userName}
              </div>

              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm"
            >
              🔐
            </button>
          )}

        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>

      </header>

      {/* ---------------- MOBILE MENU ---------------- */}

      {isMobileMenuOpen && (

        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">

          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">

              <div className="text-teal-300 text-xs lg:text-sm">
                {userName}
              </div>

              <button
                onClick={handleLogout}
                className="p-2 rounded-md bg-red-600"
              >
                ⏻
              </button>

            </div>
          ) : (
            <div className="flex justify-center">
              <Link to="/login" className="text-teal-300">
                🔐
              </Link>
            </div>
          )}

        </div>

      )}

      {/* ---------------- MAIN CONTENT ---------------- */}

      <div className="min-h-[75vh] p-4 mt-4 text-white">

        <div className="flex gap-4 mb-4">

          <button
            onClick={showSample}
            className="text-xs lg:text-sm w-40 lg:w-48 bg-blue-600 px-4 py-2 rounded"
          >
            Sample Bell Curve
          </button>

          <button
            onClick={prepareMine}
            className="text-xs lg:text-sm w-40 lg:w-48 bg-green-600 px-4 py-2 rounded"
          >
            Prepare My Bell Curve
          </button>

        </div>

        {/* INPUT MODE */}

        {mode === "input" && (

          <div className="flex flex-col mb-4">

            <input
              type="text"
              placeholder="Enter title"
              value={inputTitle}
              onChange={(e) => setInputTitle(e.target.value)}
              className="w-full text-black p-2 rounded mb-2"
            />
            <input
              type="text"
              placeholder="Enter X-Label"
              value={chartXLabel}
              onChange={(e) => setChartXLabel(e.target.value)}
              className="w-64 text-black p-2 rounded mb-2"
            />
            <input
              type="text"
              placeholder="Enter Y-Label"
              value={chartYLabel}
              onChange={(e) => setChartYLabel(e.target.value)}
              className="w-64 text-black p-2 rounded mb-2"
            />            
            <textarea
              style={{ height: "250px", width: "100%" }}
              placeholder="Enter comma separated values"
              value={inputData}
              onChange={(e) => setInputData(e.target.value)}
              className="text-white p-2 rounded mb-2 bg-gray-800"
            />

            <button
              onClick={handleShowCurve}
              className="w-48 lg:w-64 text-xs lg:text-sm bg-purple-600 mt-3 px-4 py-2 rounded"
            >
              Show Bell Curve & Insights
            </button>

            {error && (
              <div className="text-red-400 mt-2">{error}</div>
            )}

          </div>

        )}

        {chartData.length > 0 && (
          <div className="flex flex-col">
          <div className="flex justify-center max-w-8xl">
            <BellCurveChart
              data={chartData}
              title={inputTitle}
              xLabel={chartXLabel}
              yLabel={chartYLabel}
            />
          </div>
          <div className="flex justify-center">
            <BellCurveInsights data={chartData} />
          </div>
          </div>
        )}
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioVariations;